/**
 * useJobToast — listens on the shared notifications WS and shows styled
 * toasts for job lifecycle events (deal analysis, AI media generation).
 *
 * Mount this once at the app level (inside NotificationBell or a dedicated
 * provider).  It returns nothing — the side-effect is the toast queue.
 */

import { useEffect, useRef } from "react";
import { notificationRealtime } from "./notificationRealtime";
import { shouldShowToastForNotification } from "./notificationRealtime";

// ── Types ────────────────────────────────────────────────────────────────────

export type JobToastVariant = "info" | "success" | "error" | "warning";

export type JobToast = {
  id: string;
  variant: JobToastVariant;
  title: string;
  body: string;
  /** ms — 0 = persist until dismissed */
  duration: number;
};

export type PushJobToast = (toast: JobToast) => void;

// ── Config map ───────────────────────────────────────────────────────────────

type ToastConfig = {
  variant: JobToastVariant;
  duration: number;
  /** Override title if you want something shorter than the notification title */
  titleOverride?: (title: string) => string;
};

const JOB_TOAST_CONFIG: Record<string, ToastConfig> = {
  // Deal analysis
  "deal.analysis.queued":    { variant: "info",    duration: 4000 },
  "deal.analysis.started":   { variant: "info",    duration: 2500 },
  "deal.analysis.failed":    { variant: "error",   duration: 10000 },
  // Completion is now signalled by the published notification below (the separate
  // "deal.analysis.completed" event was removed to avoid double-notifying).
  "inventory.deal_published": { variant: "success", duration: 8000 },

  // AI media — started (transient, shorter)
  "ai_media.generation.started": { variant: "info",    duration: 3000 },
  "ai_media.generation.failed":  { variant: "error",   duration: 10000 },

  // AI media — completed (stored notifications already fire via generic bell toast;
  // these patterns provide styled overrides)
  "ai_media.flyer_complete":      { variant: "success", duration: 8000 },
  "ai_media.video_complete":      { variant: "success", duration: 8000 },
  "ai_media.itinerary_complete":  { variant: "success", duration: 8000 },
};

// Notification types handled here — skip in the generic bell toast loop
export const JOB_TOAST_TYPES = new Set(Object.keys(JOB_TOAST_CONFIG));

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useJobToast(pushToast: PushJobToast): void {
  const pushRef = useRef(pushToast);
  useEffect(() => { pushRef.current = pushToast; }, [pushToast]);

  useEffect(() => {
    return notificationRealtime.subscribe((msg) => {
      // followup toasts suppressed — user sees the question inline in the chat
      if (msg.type === "ai_media:progress") return;

      // ── Transient job event (not stored in bell) ──────────────────────────
      if (msg.type === "notification:job_event") {
        const notifType = String((msg as Record<string, unknown>).notif_type ?? "");
        const cfg = JOB_TOAST_CONFIG[notifType];
        if (!cfg) return;
        const title = String((msg as Record<string, unknown>).title ?? "");
        const body  = String((msg as Record<string, unknown>).body  ?? "");
        pushRef.current({
          id: `job:${notifType}:${Date.now()}`,
          variant: cfg.variant,
          title,
          body,
          duration: cfg.duration,
        });
        return;
      }

      // ── Stored notification batch ─────────────────────────────────────────
      if (msg.type !== "notification:new_batch") return;
      const batch = ((msg as Record<string, unknown>).notifications ?? []) as Array<{
        id: string;
        type?: string;
        title?: string;
        body?: string;
        read?: boolean;
      }>;

      for (const row of batch) {
        const notifType = String(row.type ?? "");
        const cfg = JOB_TOAST_CONFIG[notifType];
        if (!cfg) continue;
        if (row.read) continue;
        if (!shouldShowToastForNotification(row.id)) continue;
        pushRef.current({
          id: row.id,
          variant: cfg.variant,
          title: row.title ?? "",
          body: row.body ?? "",
          duration: cfg.duration,
        });
      }
    });
  }, []);
}
